<template>
  <v-container fluid class="pa-0">
    <!-- Fixed Header -->
    <v-card flat tile class="border-bottom sticky-header">
      <v-card-text class="pa-4 d-flex align-center">
        <v-icon large color="primary" class="mr-3">settings</v-icon>
        <div>
          <h1 class="text-h5 font-weight-bold grey--text text--darken-4 mb-0">Configuración Contable</h1>
          <span class="text-caption grey--text">Establece las cuentas por defecto para la unidad de negocio</span>
        </div>
        <v-spacer></v-spacer>
        <v-btn color="primary" :loading="saving" @click="saveSettings" depressed rounded class="px-6 font-weight-bold" height="48">
          <v-icon left>save</v-icon>
          GUARDAR CONFIGURACIÓN
        </v-btn>
      </v-card-text>
    </v-card>

    <div class="main-content pa-4">
      <!-- Selector de Unidad -->
      <v-card outlined class="mb-4 rounded-lg">
        <v-card-text class="pa-4">
          <v-row align="center" dense>
            <v-col cols="12" :md="business_filter ? 7 : 12">
              <v-autocomplete
                v-model="business_filter"
                :items="itemsBussines"
                item-text="description_long"
                item-value="id"
                outlined
                dense
                label="Unidad de Negocio Operativa"
                prepend-inner-icon="business"
                @change="loadSettings"
                :loading="loadingUnits"
                hide-details
                class="rounded-lg"
              ></v-autocomplete>
            </v-col>
            <v-col v-if="business_filter" cols="12" md="5" class="text-md-right">
              <v-btn
                color="primary"
                outlined
                dense
                class="rounded-lg font-weight-bold"
                @click="openCopyDialog"
              >
                <v-icon left>content_copy</v-icon>
                Copiar de otra unidad
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-alert v-if="!business_filter" type="info" text border="left" class="mb-4 rounded-lg">
        Selecciona una unidad para visualizar sus cuentas vinculadas.
      </v-alert>

      <v-fade-transition hide-on-leave>
        <div v-if="business_filter" :key="business_filter">
          <v-row no-gutters>
            <!-- SECCION 1: GENERAL -->
            <v-col cols="12" class="mb-4">
              <v-card outlined class="rounded-lg">
                <v-card-title class="subtitle-2 font-weight-bold blue--text text--darken-2 py-2 grey lighten-5 border-bottom">
                  <v-icon small left color="blue">account_balance</v-icon>
                  GENERAL Y BANCARIA
                </v-card-title>
                <v-card-text class="pa-4">
                  <v-row dense>
                    <v-col cols="12" md="4">
                      <v-autocomplete v-model="settings.reevaluation_chart_of_account_id" :items="accounts" item-text="displayName" item-value="id" label="Revaluación" outlined dense hide-details class="mb-2">
                        <template v-slot:selection="{ item }"><small>{{ item.code }} - {{ item.name }}</small></template>
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-autocomplete v-model="settings.bank_fee_chart_of_account_id" :items="accounts" item-text="displayName" item-value="id" label="Gastos Bancarios" outlined dense hide-details class="mb-2">
                        <template v-slot:selection="{ item }"><small>{{ item.code }} - {{ item.name }}</small></template>
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-autocomplete v-model="settings.bank_comision_chart_of_account_id" :items="accounts" item-text="displayName" item-value="id" label="Comisión Bancaria" outlined dense hide-details class="mb-2">
                        <template v-slot:selection="{ item }"><small>{{ item.code }} - {{ item.name }}</small></template>
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-autocomplete v-model="settings.close_year_chart_of_account_id" :items="accounts" item-text="displayName" item-value="id" label="Cierre de Año" outlined dense hide-details>
                        <template v-slot:selection="{ item }"><small>{{ item.code }} - {{ item.name }}</small></template>
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field v-model="settings.bank_dgii_fee" label="Tasa DGII %" type="number" outlined dense hide-details prefix="%"></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field v-model="settings.tolerance_percentage" label="Tolerancia %" type="number" outlined dense hide-details prefix="%"></v-text-field>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- SECCION 2: NOMINA -->
            <v-col cols="12" class="mb-4">
              <v-card outlined class="rounded-lg">
                <v-card-title class="subtitle-2 font-weight-bold green--text text--darken-2 py-2 grey lighten-5 border-bottom">
                  <v-icon small left color="green">groups</v-icon>
                  NÓMINA Y BENEFICIOS
                </v-card-title>
                <v-card-text class="pa-4">
                  <v-row dense>
                    <v-col cols="12" md="4">
                      <v-autocomplete v-model="settings.holding_chart_of_account_id" :items="accounts" item-text="displayName" item-value="id" label="Retenciones" outlined dense hide-details>
                        <template v-slot:selection="{ item }"><small>{{ item.code }} - {{ item.name }}</small></template>
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-autocomplete v-model="settings.bonus_chart_of_account_id" :items="accounts" item-text="displayName" item-value="id" label="Bonificaciones" outlined dense hide-details>
                        <template v-slot:selection="{ item }"><small>{{ item.code }} - {{ item.name }}</small></template>
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-autocomplete v-model="settings.saving_plan_chart_of_account_id" :items="accounts" item-text="displayName" item-value="id" label="Plan de Ahorro" outlined dense hide-details>
                        <template v-slot:selection="{ item }"><small>{{ item.code }} - {{ item.name }}</small></template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- SECCION 3: VENTAS -->
            <v-col cols="12" class="mb-4">
              <v-card outlined class="rounded-lg">
                <v-card-title class="subtitle-2 font-weight-bold orange--text text--darken-2 py-2 grey lighten-5 border-bottom">
                  <v-icon small left color="orange">sell</v-icon>
                  VENTAS Y ANTICIPOS
                </v-card-title>
                <v-card-text class="pa-4">
                  <v-row dense>
                    <v-col cols="12" md="4">
                      <v-autocomplete v-model="settings.sale_discount_product_chart_of_account_id" :items="accounts" item-text="displayName" item-value="id" label="Dcto. Productos" outlined dense hide-details>
                        <template v-slot:selection="{ item }"><small>{{ item.code }} - {{ item.name }}</small></template>
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-autocomplete v-model="settings.sale_discount_service_chart_of_account_id" :items="accounts" item-text="displayName" item-value="id" label="Dcto. Servicios" outlined dense hide-details>
                        <template v-slot:selection="{ item }"><small>{{ item.code }} - {{ item.name }}</small></template>
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-autocomplete v-model="settings.sale_prepayment_chart_of_account_id" :items="accounts" item-text="displayName" item-value="id" label="Anticipos Clientes" outlined dense hide-details>
                        <template v-slot:selection="{ item }"><small>{{ item.code }} - {{ item.name }}</small></template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- SECCION 4: COMPRAS -->
            <v-col cols="12" class="mb-8">
              <v-card outlined class="rounded-lg">
                <v-card-title class="subtitle-2 font-weight-bold purple--text text--darken-2 py-2 grey lighten-5 border-bottom">
                  <v-icon small left color="purple">shopping_bag</v-icon>
                  COMPRAS Y ANTICIPOS
                </v-card-title>
                <v-card-text class="pa-4">
                  <v-row dense>
                    <v-col cols="12" md="4">
                      <v-autocomplete v-model="settings.purchase_discount_product_chart_of_account_id" :items="accounts" item-text="displayName" item-value="id" label="Dcto. Productos" outlined dense hide-details>
                        <template v-slot:selection="{ item }"><small>{{ item.code }} - {{ item.name }}</small></template>
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-autocomplete v-model="settings.purchase_discount_service_chart_of_account_id" :items="accounts" item-text="displayName" item-value="id" label="Dcto. Servicios" outlined dense hide-details>
                        <template v-slot:selection="{ item }"><small>{{ item.code }} - {{ item.name }}</small></template>
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-autocomplete v-model="settings.purchase_prepayment_chart_of_account_id" :items="accounts" item-text="displayName" item-value="id" label="Anticipos Suplidores" outlined dense hide-details>
                        <template v-slot:selection="{ item }"><small>{{ item.code }} - {{ item.name }}</small></template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </div>
      </v-fade-transition>
    </div>

    <!-- Bottom Action Bar (More visibility) -->
    <v-footer v-if="business_filter" fixed class="pa-4 grey lighten-4 border-top" elevation="10">
      <v-spacer></v-spacer>
      <v-btn color="success" x-large :loading="saving" @click="saveSettings" width="300" class="font-weight-black elevation-4">
        <v-icon left>check_circle</v-icon>
        CONFIRMAR Y GUARDAR
      </v-btn>
      <v-spacer></v-spacer>
    </v-footer>

    <v-overlay :value="loading" z-index="999">
      <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
    </v-overlay>

    <!-- Dialog Copiar Configuración -->
    <v-dialog v-model="copyDialog" max-width="500px">
      <v-card class="rounded-lg">
        <v-card-title class="headline grey lighten-4 py-3 border-bottom d-flex align-center">
          <v-icon left color="primary">content_copy</v-icon>
          <span>Copiar Configuración</span>
        </v-card-title>
        <v-card-text class="pa-4 pt-6">
          <p class="text-body-2 grey--text text--darken-2 mb-4">
            Selecciona la unidad de negocio desde la cual deseas copiar la configuración contable. Los campos se rellenarán automáticamente con dicha información para su revisión y guardado.
          </p>
          <v-autocomplete
            v-model="source_business_unit_id"
            :items="availableSourceUnits"
            item-text="description_long"
            item-value="id"
            outlined
            dense
            label="Unidad de Negocio Origen"
            prepend-inner-icon="business"
            hide-details
            class="rounded-lg"
          ></v-autocomplete>
        </v-card-text>
        <v-card-actions class="pa-4 grey lighten-5 border-top">
          <v-spacer></v-spacer>
          <v-btn text @click="copyDialog = false">Cancelar</v-btn>
          <v-btn
            color="primary"
            depressed
            class="rounded-lg font-weight-bold px-4"
            :disabled="!source_business_unit_id"
            :loading="loadingCopy"
            @click="confirmCopy"
          >
            <v-icon left small>content_copy</v-icon>
            Copiar Configuración
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
export default {
  name: 'AccountsSettings',
  data: () => ({
    business_filter: null,
    itemsBussines: [],
    accounts: [],
    loading: false,
    loadingUnits: false,
    saving: false,
    copyDialog: false,
    source_business_unit_id: null,
    loadingCopy: false,
    settings: {
      business_unit_id: null,
      reevaluation_chart_of_account_id: null,
      bank_fee_chart_of_account_id: null,
      bank_dgii_fee: 0,
      holding_chart_of_account_id: null,
      bonus_chart_of_account_id: null,
      saving_plan_chart_of_account_id: null,
      sale_discount_product_chart_of_account_id: null,
      sale_discount_service_chart_of_account_id: null,
      purchase_discount_product_chart_of_account_id: null,
      purchase_discount_service_chart_of_account_id: null,
      sale_prepayment_chart_of_account_id: null,
      purchase_prepayment_chart_of_account_id: null,
      tolerance_percentage: 0,
      close_year_chart_of_account_id: null,
      bank_comision_chart_of_account_id: null,
    },
    defaultSettings: {}
  }),

  computed: {
    availableSourceUnits() {
      return this.itemsBussines.filter(u => u.id !== this.business_filter);
    }
  },

  async created() {
    this.defaultSettings = { ...this.settings };
    await this.initialize();
  },

  methods: {
    async initialize() {
      this.loading = true;
      try {
        this.itemsBussines = [...(this.$store.state.auth.bussines_units || [])];
        const response = await this.api_Get("accounting/accountsUsable");
        const data = await response.json();
        this.accounts = data.map(acc => ({
          ...acc,
          displayName: `${acc.code} - ${acc.name}`
        }));
      } catch (error) {
        this.$toast.error("Error al inicializar");
      } finally {
        this.loading = false;
      }
    },

    async loadSettings() {
      if (!this.business_filter) return;
      this.loading = true;
      try {
        const response = await this.api_Get(`accounting/accounting_settings/${this.business_filter}`);
        const data = await response.json();
        if (data && data.length > 0) {
          this.settings = { ...data[0] };
          this.defaultSettings = { ...data[0] }; // Store initial state for comparison
        } else {
          this.settings = { ...this.settings, business_unit_id: this.business_filter };
          this.defaultSettings = { ...this.settings, business_unit_id: this.business_filter };
        }
      } catch (error) {
        this.$toast.error("Error al cargar datos");
      } finally {
        this.loading = false;
      }
    },

    async saveSettings() {
      if (!this.business_filter) return;
      this.saving = true;
      try {
        // Construct payload with ONLY changed fields
        const payload = {
          business_unit_id: this.business_filter
        };

        const fields = Object.keys(this.settings);
        let hasChanges = false;

        fields.forEach(key => {
          if (key === 'business_unit_id' || key === 'effective_date') return;
          
          const currentValue = this.settings[key];
          const initialValue = this.defaultSettings[key];

          if (currentValue !== initialValue && currentValue !== null && currentValue !== undefined) {
            payload[key] = (key === 'bank_dgii_fee' || key === 'tolerance_percentage') 
              ? parseFloat(currentValue) || 0 
              : currentValue;
            hasChanges = true;
          }
        });

        if (!hasChanges) {
          this.$toast.info("No hay cambios para guardar.");
          this.saving = false;
          return;
        }

        const response = await this.api_Post("accounting/add_accounting_settings", payload);
        if (response.status === 200) {
          this.$toast.success("Configuración actualizada");
          await this.loadSettings();
        } else {
          this.$toast.error("Error al guardar");
        }
      } catch (error) {
        this.$toast.error("Fallo de red");
      } finally {
        this.saving = false;
      }
    },

    openCopyDialog() {
      this.source_business_unit_id = null;
      this.copyDialog = true;
    },

    async confirmCopy() {
      if (!this.source_business_unit_id) return;
      this.loadingCopy = true;
      try {
        const response = await this.api_Get(`accounting/accounting_settings/${this.source_business_unit_id}`);
        const data = await response.json();
        if (data && data.length > 0) {
          const sourceSettings = data[0];
          Object.keys(this.settings).forEach(key => {
            if (key !== 'business_unit_id' && key !== 'id' && sourceSettings[key] !== undefined && sourceSettings[key] !== null) {
              this.settings[key] = sourceSettings[key];
            }
          });
          this.$toast.info("Configuración copiada. Revisa los campos y guarda para confirmar.");
          this.copyDialog = false;
        } else {
          this.$toast.warning("La unidad seleccionada no tiene configuración contable registrada.");
        }
      } catch (error) {
        this.$toast.error("Error al obtener la configuración de la unidad origen");
      } finally {
        this.loadingCopy = false;
      }
    }
  }
};
</script>

<style scoped>
.sticky-header {
  position: sticky;
  top: 0;
  z-index: 5;
  background: white;
}

.main-content {
  background-color: #f5f5f5;
  min-height: calc(100vh - 80px);
}

.border-bottom {
  border-bottom: 1px solid #e0e0e0 !important;
}

.border-top {
  border-top: 1px solid #e0e0e0 !important;
}

.rounded-lg {
  border-radius: 12px !important;
}

.account-selector >>> .v-select__selection {
  font-size: 0.85rem !important;
}
</style>
